import { useMemo } from 'react';

import { isValidSubEnvironment } from '@change-corgi/config/environments';
import { getWindow } from '@change-corgi/core/window';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { TypesenseConfig } from '#config/typesense';
import { decorateForDevProxying, getTypesenseConfig } from '#config/typesense';

export function useTypesenseConfig(): { config: TypesenseConfig; namespace?: string } {
	const {
		environment: { getApiSubEnvironment, getApiEnvironment, getEnvironment, getApiDemoEnvironmentTarget },
	} = useUtilityContext();
	const apiEnvironment = getApiEnvironment();
	const apiSubEnvironment = getApiSubEnvironment();

	const { config, namespace } = useMemo(() => {
		const isValidSubEnv = isValidSubEnvironment(apiSubEnvironment);
		if (isValidSubEnv) {
			return {
				config: getTypesenseConfig(apiSubEnvironment),
				namespace: apiEnvironment === 'demo' ? apiSubEnvironment : undefined,
			};
		}
		// this handles personalized demo environments that point to a full demo environment (e.g. jlou -> cyan)
		// for those, we get the target env name using an env variable that is set on window by the server, and made available via the environment context
		const target = getApiDemoEnvironmentTarget();
		if (target) {
			return {
				config: getTypesenseConfig(target),
				namespace: target,
			};
		}
		throw new Error('Could not determine Typesense config and namespace');
	}, [apiSubEnvironment, apiEnvironment, getApiDemoEnvironmentTarget]);

	return useMemo(() => {
		const isDevProxying =
			typeof window !== 'undefined' && getEnvironment() === 'development' && apiSubEnvironment !== 'development';
		if (isDevProxying) {
			return {
				config: decorateForDevProxying(config, getWindow().location),
				namespace,
			};
		}
		return { config, namespace };
	}, [config, namespace, getEnvironment, apiSubEnvironment]);
}
